import { LineItem } from '../../core';
import { formatName } from '../../core/user';
import { SalesTermsMapper } from '../../mappers';
import {
  BillLineItem,
  BillOrderAttachmentType,
  BillStatus,
  BillType,
  BusinessEntity,
  Contact,
  CustomFields,
  ListTemplate,
  Membership,
  Note,
  Organization,
  Payment,
  Place,
  SalesTerms,
  Shipment,
} from '../../types';
import Money from '../../utils/Money';
import { AddressComponent, AddressTemplateType } from '../components/address.template';
import { documentNotes } from '../components/documentNotes.template';
import { OrganizationComponent, OrganizationTemplateType } from '../components/organization.template';
import { templatizeProductLineItem } from '../components/productsGrid.template';
import { ShipmentComponent, ShipmentTemplateType } from '../components/shipment.template';
import { DocumentTypePopulator } from '../DocumentGenerator';

import {
  applyTableMetadata,
  formatCurrency,
  formatDate,
  formatDateVerbose,
  formatDecimal,
  getPrimaryBusiness,
} from './formatters';

export type BillInputType = {
  id: number;
  slug: string;
  type: BillType;

  // Organization
  organization: Organization;

  // Details
  status: BillStatus;
  reference?: string;
  paymentTerms: SalesTerms;

  // Counter party
  businessEntity?: BusinessEntity;
  contact?: Contact;
  billingAddress?: Place;

  invoiceDate?: string | Date;
  dueDate?: string | Date;

  // Commission
  consignmentCommissionRate?: number;
  consignmentCommissionAmount?: number;

  receipts: Payment[];
  adjustments: Array<{ id: number; amount: number; memo: string; reason: string }>;
  lineItems: BillLineItem[];

  attachedOrders: AttachedOrder[];

  notes: Note[];
  customFields?: CustomFields;
};

type AttachedSalesOrderType = {
  slug?: string;
  shipDate?: string | Date;
  shipment: Shipment;
  subsidiary?: Place;
  salesPerson?: Membership;
  customerPo?: string;
  submittedAt?: string | Date;
  customFields?: CustomFields;
  currency?: string;
};

type AttachedGrowerProductType = { slug?: string; shipment: Shipment; customFields?: CustomFields };

type AttachedBuySellOrderType = {
  slug?: string;
  shipment: Shipment;
  subsidiary?: Place;
  customerPo?: string;
  broker?: Membership;
  orderDate?: string | Date;
  customFields?: CustomFields;
  currency?: string;
};

type GenericAttachedOrder = AttachedSalesOrderType & AttachedGrowerProductType & AttachedBuySellOrderType;

type AttachedOrder = {
  type: BillOrderAttachmentType;
  salesOrder?: {
    slug?: string;
    shipDate?: string | Date;
    shipment: Shipment;
    subsidiary?: Place;
    salesPerson?: Membership;
    customerPo?: string;
    submittedAt?: string | Date;
  };
  growerProduct?: AttachedGrowerProductType;
  buySellOrder?: AttachedBuySellOrderType;
};

const getOrderLabel = (attachedOrder: Omit<AttachedOrder, 'type'>, organization: Organization) => {
  if (attachedOrder.salesOrder) {
    const soLabel = organization.salesOrderDocumentPrefix ?? 'SO';

    return `${soLabel} #${attachedOrder.salesOrder.slug}`;
  }

  if (attachedOrder.growerProduct) {
    return `GP #${attachedOrder.growerProduct.slug}`;
  }

  if (attachedOrder.buySellOrder) {
    return `BSO #${attachedOrder.buySellOrder.slug}`;
  }

  return '';
};

export type BillInvoiceTemplateDataType = Partial<{
  billNumber: string;
  orgLogo: string;
  organization: OrganizationTemplateType;
  businessAddress: AddressTemplateType;
  phoneNumber: string;
  email: string;
  payTo: string;
  billingAddress: AddressTemplateType;
  lineItems: ListTemplate<{
    description?: string;
    unitCount?: string;
    unitPrice?: string;
    subtotal?: string;
    category?: string;
  }>;
  totalAmountDue: string;
  totalPaid: string;
  invoiceDate: string;
  dueDate: string;
  dueDateVerbose: string;
  reference: string;
  paymentTerms: string;
  orderLabel: string;
  salesPerson: string;
  customerPo: string;
  shipment: ShipmentTemplateType;
  notes: ListTemplate<{ text: string }>;
}>;

export const BillInvoiceTemplate: DocumentTypePopulator<
  BillInputType,
  { productDescriptionTemplate?: string },
  BillInvoiceTemplateDataType
> = {
  populate: (bill, { type, templateId, productDescriptionTemplate }, globalOptions) => {
    if (!bill) {
      return {};
    }

    const {
      slug,
      receipts,
      invoiceDate,
      dueDate,
      reference,
      paymentTerms,
      billingAddress,
      attachedOrders,
      organization,
      lineItems,
      notes,
    } = bill;

    const totalPaid = receipts.reduce((acc, payment) => acc + payment.paymentAmount || 0, 0);

    const salesOrder = attachedOrders.find((ao) => ao.salesOrder && ao.type === 'ORDER')?.salesOrder;
    const buySellOrder = attachedOrders.find((ao) => ao.buySellOrder && ao.type === 'BSO')?.buySellOrder;
    const growerProduct = attachedOrders.find((ao) => ao.growerProduct && ao.type === 'CONSIGNMENT')?.growerProduct;

    const orderLabel = getOrderLabel({ salesOrder, growerProduct, buySellOrder }, organization);

    // only one should be attached to ORDER type bills
    const order = (salesOrder ?? growerProduct ?? buySellOrder) as GenericAttachedOrder;
    const salesPerson = salesOrder?.salesPerson ?? buySellOrder?.broker;
    const customerPo = salesOrder?.customerPo ?? buySellOrder?.customerPo;
    const orderDate = salesOrder?.submittedAt ?? buySellOrder?.orderDate;

    const totalAmountDue = lineItems
      .filter((lineItem) => lineItem.sourceBuySellOrderLineItem ?? lineItem.sourceLineItem ?? lineItem.sourceExpense)
      .filter(({ sourceExpense }) =>
        sourceExpense
          ? !(sourceExpense?.accountLevel === 'ACCOUNTS_RECEIVABLE' && sourceExpense?.includedInDlvPrice)
          : true
      )
      .reduce((acc, lineItem) => {
        return acc + lineItem.unitPrice * lineItem.unitCount;
      }, 0);

    const productLineItems = lineItems
      .filter((lineItem) => lineItem.sourceBuySellOrderLineItem ?? lineItem.sourceLineItem)
      .map((lineItem) => ({
        billLineItem: lineItem,
        ...(lineItem.sourceBuySellOrderLineItem ?? lineItem.sourceLineItem),
        // work around to get bso line items to map appropriately.
        unitsPicked: lineItem.sourceBuySellOrderLineItem
          ? lineItem.sourceBuySellOrderLineItem.unitsInvoiced
          : lineItem.sourceLineItem.unitsPicked,
      }));

    // i just want this to work for now
    let products: any = productLineItems;

    if (salesOrder) {
      products = LineItem.Utils.flattenForDisplay(productLineItems as any, true);
    }

    const mapBillLineItem = (lineItem: any) => {
      const unitWeight = (lineItem.external ? lineItem.weightShipped : lineItem.weightPicked) ?? 0;
      const unitCount = (lineItem.external ? lineItem.unitsShipped : lineItem.unitsPicked) ?? 0;
      // Basis MUST match persistence, else the row won't reconcile with the
      // header total (which sums the persisted BillLineItem.unitCount):
      //
      //   persistence  billOrderFlow.repo.ts
      //     parentLineItem?.eByW ?? lineItem.eByW  (no variableWeight)
      //          │  same predicate + same shipped/picked fields
      //          ▼
      //   priceByCount  ==  BillLineItem.unitCount  (persisted)
      //          │
      //          ▼
      //   row subtotal  ==  productsSubtotal  ==  totalAmountDue
      //
      // Weight when entered-by-weight (own for the external/unsplit path — e.g.
      // bill 171358 — or the parent for picked splits), else units. NOT keyed on
      // variableWeight: the document shows what was billed; a variableWeight line
      // entered by units is a persistence-side call (see PR).
      const priceByCount =
        (lineItem.parentLineItem?.enteredByWeight ?? lineItem.enteredByWeight) ? unitWeight : unitCount;

      const mappedChildren = lineItem.childrenLineItems?.map((child: any) => {
        const childWeight = (child.external ? child.weightShipped : child.weightPicked) ?? 0;
        const childUnitCount = (child.external ? child.unitsShipped : child.unitsPicked) ?? 0;
        const priceByCount =
          (child.parentLineItem?.enteredByWeight ?? child.enteredByWeight) ? childWeight : childUnitCount;

        return {
          item: {
            ...templatizeProductLineItem(child, {
              currency: order.currency ?? 'USD',
              descriptionTemplate: productDescriptionTemplate,
            }),
            // count column shows the priced basis, so count × price == subtotal
            unitCount: formatDecimal(priceByCount),
            unitPrice: formatCurrency(Money.toDollars(child.billLineItem.unitPrice)),
            subtotal: formatCurrency(Money.toDollars(child.billLineItem.unitPrice * priceByCount)),
          },
        };
      });

      // parent line items dont have bill line items, the price will be the same as the first child.
      let billLineItem = lineItem.billLineItem ?? lineItem.childrenLineItems?.[0]?.billLineItem;

      return {
        ...templatizeProductLineItem(lineItem, {
          currency: order.currency ?? 'USD',
          descriptionTemplate: productDescriptionTemplate,
        }),
        // count column shows the priced basis, so count × price == subtotal
        unitCount: formatDecimal(priceByCount),
        unitPrice: formatCurrency(Money.toDollars(billLineItem?.unitPrice)),
        subtotal: formatCurrency(Money.toDollars((billLineItem?.unitPrice ?? 0) * priceByCount)),
        children: mappedChildren,
      };
    };

    const formattedProducts = products?.map(mapBillLineItem) ?? [];
    const formattedProductsNested = LineItem.Utils.nestForGrid(productLineItems as any).map(mapBillLineItem);

    const charges = lineItems
      .filter((lineItem) => {
        const dlvCharge =
          lineItem.sourceExpense?.accountLevel === 'ACCOUNTS_RECEIVABLE' && lineItem.sourceExpense?.includedInDlvPrice;

        return lineItem.sourceExpense && !dlvCharge;
      })
      .map((lineItem) => {
        const unitCount = lineItem.unitCount;

        return {
          description: lineItem.description,
          category: lineItem.sourceExpense!.expenseType?.name,
          unitCount: formatDecimal(unitCount),
          unitPrice: formatCurrency(Money.toDollars(lineItem.unitPrice)),
          subtotal: formatCurrency(Money.toDollars(lineItem.unitPrice * unitCount)),
        };
      });

    const productsSubtotal = lineItems
      .filter((lineItem) => lineItem.sourceLineItem ?? lineItem.sourceBuySellOrderLineItem)
      .reduce((acc, lineItem) => acc + lineItem.unitPrice * lineItem.unitCount, 0);

    const chargesSubtotal = lineItems
      .filter((lineItem) => {
        if (!lineItem.sourceExpense) {
          return false;
        }

        const dlvCharge =
          lineItem.sourceExpense.accountLevel === 'ACCOUNTS_RECEIVABLE' && lineItem.sourceExpense.includedInDlvPrice;

        return !dlvCharge;
      })
      .reduce((acc, lineItem) => acc + lineItem.unitPrice * lineItem.unitCount, 0);

    return {
      billNumber: slug,

      orgLogo: organization?.logoUrl,
      organization: OrganizationComponent.templatize({
        organization,
        subsidiary: order?.subsidiary,
        customOrgName: globalOptions.customOrgName,
      }),

      businessAddress: AddressComponent.templatize(getPrimaryBusiness(organization)),
      phoneNumber: getPrimaryBusiness(organization)?.phone,
      email: salesPerson?.user?.email,
      payTo: bill.businessEntity?.name,

      billingAddress: AddressComponent.templatize(billingAddress),

      products: applyTableMetadata(formattedProducts),
      productsNested: applyTableMetadata(formattedProductsNested),
      productsSubtotal: formatCurrency(Money.toDollars(productsSubtotal)),
      chargesSubtotal: formatCurrency(Money.toDollars(chargesSubtotal)),
      charges: applyTableMetadata(charges),
      totalAmountDue: formatCurrency(Money.toDollars(totalAmountDue)),
      totalPaid: formatCurrency(Money.toDollars(totalPaid)),
      invoiceDate: formatDate(invoiceDate),
      dueDate: formatDate(dueDate),
      dueDateVerbose: formatDateVerbose(dueDate),

      reference,
      paymentTerms: SalesTermsMapper.getLabel(paymentTerms),

      orderLabel,
      orderDate: formatDate(orderDate),
      salesPerson: formatName(salesPerson?.user, ''),
      customerPo,
      shipment: order
        ? ShipmentComponent.templatize({
            ...order?.shipment,
            shipDate: order?.shipDate ?? order?.shipment.shipDate,
            destination: order?.shipment.place ?? order?.shipment.destination,
          })
        : undefined,
      orderCustomFields: order?.customFields,
      notes: documentNotes(notes, templateId, type),
      customFields: bill.customFields,
    };
  },
};
